import { cn } from "@/lib/utils";
import { Eye, EyeOff, Lock, XCircle } from "lucide-react";
import React, { useState, FocusEvent, useEffect, useRef, useId } from "react";

interface FloatingLabelInputProps {
  type: string;
  label: string;
  value: string;
  required?: boolean;
  placeholder?: string;
  errorMessage?: string;
  readonly?: boolean;
  /** Greys the field out and shows a lock instead of the reveal toggle. */
  disabled?: boolean;
  borderless?: boolean;
  /** Search-style field: the hint sits inline as a placeholder and never floats. */
  inlineLabel?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onEnter?: () => void;
  /** Optional: fired when the password show/hide eye is toggled. `visible` = resulting state. */
  onToggleVisibility?: (visible: boolean) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  maxLength?: number;
  min?: string;
  max?: string;
  "aria-describedby"?: string;
  /** Invalid without a message to show. An `errorMessage` forces "true" regardless. */
  "aria-invalid"?: boolean | "true" | "false";
  "aria-label"?: string;
  // ARIA combobox attributes for accessibility
  role?: string;
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
  "aria-activedescendant"?: string;
  "aria-autocomplete"?: "none" | "inline" | "list" | "both";
}

const FloatingLabelInput = React.forwardRef<HTMLInputElement, FloatingLabelInputProps>(({
  type,
  label,
  value,
  required,
  placeholder,
  errorMessage,
  maxLength,
  readonly,
  disabled = false,
  borderless = false,
  inlineLabel = false,
  onChange,
  onFocus,
  onBlur,
  onClear,
  onEnter,
  onToggleVisibility,
  onKeyDown,
  min,
  max,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-label": ariaLabel,
  role,
  "aria-expanded": ariaExpanded,
  "aria-controls": ariaControls,
  "aria-activedescendant": ariaActivedescendant,
  "aria-autocomplete": ariaAutocomplete,
}, forwardedRef) => {
  const [active, setActive] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const internalRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  // Merge refs to support both internal usage and forwarded ref
  const inputRef = React.useMemo(() => {
    if (typeof forwardedRef === 'function') {
      return (node: HTMLInputElement | null) => {
        (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        forwardedRef(node);
      };
    } else if (forwardedRef) {
      return (node: HTMLInputElement | null) => {
        (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      };
    }
    return internalRef;
  }, [forwardedRef]);

  // useEffect(() => {
  // if (value) {
  // setActive(true);
  // }
  // }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setActive(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    // if (inputValue.length === 0) {
    setActive(false);
    // }
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    onChange(event);
  };

  const handleClear = () => {
    setInputValue("");
    // Create a synthetic event to properly trigger onChange
    const event = {
      target: {
        value: "",
        name: internalRef.current?.name || "",
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "flex justify-center items-center relative h-[48px] rounded-[8px] gap-4 px-3",
          borderless
            ? "border-0"
            : disabled
              ? "border border-border-token-neutral-minimal bg-bg-neutral-secondary-disabled"
              : errorMessage
                ? "border border-border-token-error-subtle bg-bg-error-tertiary-default"
                : active
                  ? "border-2 border-primary-500 bg-container-brand-minimal"
                  : inputValue.length > 0
                    ? "border-2 border-border-token-neutral-minimal bg-container-brand-minimal"
                    : "border border-border-token-neutral-subtle",
        )}
      >
        <input
          ref={inputRef}
          className={cn(
            "outline-none w-full rounded text-base md:text-[13px] transition-all duration-200 ease-in-out bg-transparent",
            inlineLabel
              ? "placeholder:text-fg-neutral-subtle"
              : inputValue.length > 0 || active || type === "datetime-local"
                ? "pt-3"
                : "",
          )}
          placeholder={inlineLabel ? placeholder || label : undefined}
          aria-label={inlineLabel ? label : ariaLabel}
          id={inputId}
          type={showPwd ? "text" : type.toLowerCase()}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={readonly || disabled}
          maxLength={maxLength}
          onKeyDown={onKeyDown}
          onKeyPress={(event) => {
            if (event.key === "Enter" && onEnter) {
              onEnter();
            }
          }}
          aria-invalid={errorMessage ? "true" : (ariaInvalid ?? "false")}
          aria-describedby={
            [
              errorMessage ? `${inputId}-error` : "",
              ariaDescribedBy || "",
            ]
              .filter(Boolean)
              .join(" ") || undefined
          }
          min={min}
          max={max}
          role={role}
          aria-expanded={ariaExpanded}
          aria-controls={ariaControls}
          aria-activedescendant={ariaActivedescendant}
          aria-autocomplete={ariaAutocomplete}
        />
        {!inlineLabel && (
          <label
            htmlFor={inputId}
            className={cn(
              "absolute left-3 transition-all duration-200 ease-in-out cursor-text",
              inputValue.length > 0 || active || type === "datetime-local"
                ? "label-s-regular top-[6px]"
                : "label-m-medium",
              disabled
                ? "text-fg-neutral-minimal"
                : errorMessage
                  ? "text-fg-error-strong"
                  : inputValue.length > 0 || active || type === "datetime-local"
                    ? "!text-fg-brand-subtle"
                    : "text-fg-neutral-subtle",
            )}
          >
            {placeholder || label}
            {" "}
            {required && <span className="text-fg-brand-subtle">*</span>}
          </label>
        )}
        {type !== "Password" && type !== "datetime-local" && !readonly && !disabled && (
          <button
            type="button"
            className={cn(
              "flex items-center justify-center w-11 h-11 cursor-pointer bg-transparent border-0",
              inputValue.length === 0 && "invisible"
            )}
            aria-label="Clear"
            onClick={() => {
              setActive(false);
              handleClear();
            }}
            disabled={inputValue.length === 0}
          >
            <XCircle
              size={24}
              strokeWidth={1.5}
              aria-hidden="true"
              className={cn(
                errorMessage
                  ? ""
                  : inlineLabel
                    ? "text-fg-neutral-strong"
                    : "text-fg-brand-subtle",
              )}
            />
          </button>
        )}
        {/* A locked field is not revealable, so the lock replaces the eye. */}
        {type === "Password" && disabled && (
          <Lock
            size={20}
            strokeWidth={1.5}
            className="shrink-0 text-fg-neutral-minimal"
            aria-hidden="true"
          />
        )}
        {type === "Password" && !disabled && (
          <button
            type="button"
            className={cn(
              "flex items-center justify-center w-11 h-11 cursor-pointer bg-transparent border-0",
              inputValue.length === 0 && "invisible"
            )}
            onClick={() => {
              const next = !showPwd;
              onToggleVisibility?.(next);
              setShowPwd(next);
            }}
            aria-label={showPwd ? "Hide password" : "Show password"}
            disabled={inputValue.length === 0}
          >
            {!showPwd ? (
              <Eye
                size={24}
                strokeWidth={1.5}
                className={errorMessage ? "" : "text-fg-brand-subtle"}
              />
            ) : (
              <EyeOff
                size={24}
                strokeWidth={1.5}
                className={errorMessage ? "" : "text-fg-brand-subtle"}
              />
            )}
          </button>
        )}
      </div>
      {errorMessage && (
        <div id={`${inputId}-error`} className="body-s-regular mt-2 text-fg-error-subtle" role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
});

FloatingLabelInput.displayName = 'FloatingLabelInput';

export default FloatingLabelInput;
